import axios from "axios";
import dayjs from "dayjs";

/**
 * Crea una instancia de Axios reutilizable con lógica dedicada de almacenamiento,
 * recuperación y actualización de tokens, aislada por claves de almacenamiento únicas.
 *
 * @param {AuthAxiosConfig} config - Configuración para la instancia.
 * @returns {import("axios").AxiosInstance} Una instancia de Axios configurada.
 */
export const createAuthAxiosInstance = (config) => {
  const { tokenKey, expiryKey, tokenRefreshService, baseURL, timeout = 30000 } = config;

  // Gestión de LocalStorage (Aislada a las claves de esta instancia) ---
  /**
   * @param {string} token
   * @param {number} expiresIn
   */
  const saveToken = (token, expiresIn) => {
    const expiry = dayjs().add(expiresIn - 60, "second");
    localStorage.setItem(tokenKey, token);
    localStorage.setItem(expiryKey, expiry.toISOString());
  };

  /**
   * @returns {string | null}
   */
  const loadToken = () => {
    const token = localStorage.getItem(tokenKey);
    const expiry = localStorage.getItem(expiryKey);

    if (token && expiry && dayjs().isBefore(dayjs(expiry))) {
      return token;
    }
    return null;
  };

  // Lógica de Recuperación/Actualización de Token (Usa el servicio inyectado) ---
  /**
   * @returns {Promise<string>}
   */
  const getAuthToken = async () => {
    const cached = loadToken();
    if (cached) return cached;

    // Llama a la lógica de actualización específica proporcionada por el usuario
    const response = await tokenRefreshService();

    const { access_token, expires_in } = response;

    saveToken(access_token, expires_in);

    return access_token;
  };

  // Configuración de la Instancia de Axios ---
  // timeout: sin esto, axios espera indefinidamente -- un endpoint colgado
  // nunca resuelve ni rechaza, así que el retry/backoff de executeWithRetry
  // (syncService.js) nunca se activa (solo reacciona a promesas rechazadas).
  const api = axios.create({
    baseURL,
    timeout,
    headers: { "Content-Type": "application/json" },
  });

  // Promesa para manejar peticiones 401 concurrentes, asegurando que solo se produce una actualización
  let refreshPromise = null;

  // Interceptor de Solicitud: Adjunta el token actual
  api.interceptors.request.use(
    async (config) => {
      try {
        const token = await getAuthToken();
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      } catch (error) {
        console.error(
          `[${tokenKey}] Fallo al recuperar el token en el interceptor de solicitud:`,
          error
        );
        return Promise.reject(error);
      }
    },
    (error) => Promise.reject(error)
  );

  // Interceptor de Respuesta: Maneja errores 401 No Autorizado
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      const status = error.response?.status;

      // Verifica si es un 401 y no es una petición de reintento
      if (status === 401 && originalRequest && !originalRequest.__isRetryRequest) {
        originalRequest.__isRetryRequest = true;

        // Limpiar el token antiguo antes de iniciar el intento de actualización
        localStorage.removeItem(tokenKey);
        localStorage.removeItem(expiryKey);

        // Debounce: Si una actualización ya está en curso, espera a que termine
        if (!refreshPromise) {
          refreshPromise = getAuthToken()
            .then((newToken) => {
              refreshPromise = null; // Borrar la promesa en caso de éxito
              return newToken;
            })
            .catch((err) => {
              refreshPromise = null; // Borrar la promesa en caso de fallo
              // Manejar el cierre de sesión completo o la redirección aquí si es necesario
              throw err;
            });
        }

        try {
          const newToken = await refreshPromise;

          // Actualizar el encabezado y reintentar la solicitud original
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
          }

          return api(originalRequest);
        } catch (refreshError) {
          console.error(
            `[${tokenKey}] La actualización del token falló permanentemente:`,
            refreshError
          );
          return Promise.reject(refreshError);
        }
      }

      // Para errores que no son 401 o solicitudes ya reintentadas, rechazar
      return Promise.reject(error);
    }
  );

  return api;
};
